<script setup lang="ts">
import Accordion from "@/components/Accordion.vue"
import AccordionItem from "@/components/AccordionItem.vue"
import type { ChampionshipResultDto } from "@/model/championship/championship-result-dto"
import { $formatResult } from "@/utils"
import { useHttpGet } from "@/vf"
import { computed } from "vue"

const props = defineProps<{
    element: any
}>()

const url = computed(() => {
    const query = new URLSearchParams()

    if (props.element.config.selectedChampionshipEventDay) {
        query.set("eventDay", props.element.config.selectedChampionshipEventDay)
    }

    const queryString = query.toString()

    return "/scoring/score/public/" + props.element.config.selectedChampionship + (queryString ? "?" + queryString : "")
})

const { data: championshipResult } = await useHttpGet<ChampionshipResultDto>(url)
</script>

<template>
    <Accordion v-if="championshipResult" open-first>
        <AccordionItem
            v-if="championshipResult.withoutScore && championshipResult.withoutScore.length > 0"
            id="exhibition"
            :title="$t('@cms:championshipResults.exhibitionOnly')"
        >
            <div class="d-none d-md-block">
                <table class="table table-striped">
                    <tbody>
                        <tr>
                            <th>{{ $t("@cms:championshipResults.division") }}</th>
                            <th>{{ $t("@cms:championshipResults.team") }}</th>
                            <th class="text-muted">{{ $t("@cms:championshipResults.club") }}</th>
                        </tr>
                        <tr v-for="team in championshipResult.withoutScore" :key="team.name">
                            <td>{{ team.division }}</td>
                            <td>{{ team.name }} ({{ team.country }})</td>
                            <td class="text-muted">{{ team.club }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-block d-md-none">
                <div
                    v-for="team in championshipResult.withoutScore"
                    :key="team.name"
                    class="py-3"
                    style="border-bottom: 1px solid #ccc"
                >
                    <div>{{ team.name }} ({{ team.country }})</div>
                    <div class="text-muted small">{{ team.club }}</div>
                </div>
            </div>
        </AccordionItem>

        <AccordionItem
            v-for="(category, index) in championshipResult.withScore"
            :key="category.name"
            :id="`category-${index}`"
            :title="category.name || '-'"
        >
            <div class="d-none d-md-block">
                <table class="table table-striped">
                    <tbody>
                        <tr>
                            <th>{{ $t("@cms:championshipResults.place") }}</th>
                            <th>{{ $t("@cms:championshipResults.results") }}</th>
                            <th>{{ $t("@cms:championshipResults.team") }}</th>
                            <th class="text-muted">{{ $t("@cms:championshipResults.club") }}</th>
                        </tr>
                        <tr v-for="team in category.teams" :key="team.name">
                            <td>{{ team.place }}</td>
                            <td>{{ $formatResult(team.score ?? 0, 2) }}</td>
                            <td>{{ team.name }} ({{ team.country }})</td>
                            <td class="text-muted">{{ team.club }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-block d-md-none">
                <div v-for="team in category.teams" :key="team.name" style="border-bottom: 1px solid #ccc">
                    <div class="pt-3 d-flex flex-row align-items-center mb-1">
                        <div class="text-center" style="width: 80px; flex-shrink: 0">
                            <div class="h3 m-0">{{ team.place }}.</div>
                        </div>
                        <div>{{ team.name }} ({{ team.country }})</div>
                    </div>
                    <div class="pb-3 d-flex flex-row align-items-end">
                        <div class="small text-muted text-center" style="width: 80px; flex-shrink: 0">
                            {{ $formatResult(team.score ?? 0, 2) }}
                        </div>
                        <div class="text-muted small">{{ team.club }}</div>
                    </div>
                </div>
            </div>
        </AccordionItem>
    </Accordion>
</template>
